// Rail hubs (design_handoff_rail_fold): pages that belong together share one rail link. A hub
// opens the page last used in it (this device), and a tab bar above the page switches between
// its pages. The pages themselves are untouched: each tab clicks the page's own rail button,
// which still lives in the DOM (off stage in #rail-hub-members, or on the rail when the user
// gives it its own link in Customize menu), so every .click() caller keeps working.
//
// Selection follows whatever opened the page — a hub, a tab, a popped-out rail link, a search
// result or another page's goTo — because it is worked out from the page on screen.

import { escapeHTML } from './utilities';

// Each tab: key = the page's rail button id; page = the page-view id it shows (Moorings modes add
// mode = the Mooring Field hash). wire: app.js wires the click (the buttons the Moorings and Fuel
// flyouts used to build); hash: set on the page's iframe after opening; title: the page title on
// phones (the top bar); desc: the tab's tooltip; external: opens elsewhere, never "last used";
// noTab: a mode of another tab's page, reached from that page's own switch (and search), not a tab.
export const HUBS = {
  'hub-dashboard': {
    label: 'Dashboard', icon: '/assets/dashboard.svg',
    tabs: [
      { key: 'dashboard-open-button', label: 'Overview', page: 'dashboard' },
      { key: 'reports-open-button', label: 'Reports', page: 'reports' },
    ],
  },
  'hub-equipment': {
    label: 'Equipment', icon: '/assets/repair-icon.svg',
    tabs: [
      { key: 'equipment-open-button', label: 'Equipment', page: 'equipment' },
      { key: 'maintenance-open-button', label: 'Maintenance', page: 'maintenance' },
      { key: 'battery-open-button', label: 'Batteries', page: 'battery' },
    ],
  },
  'hub-storage': {
    label: 'Storage', icon: '/assets/sailboat.png',
    tabs: [
      { key: 'slips-open-button', label: 'Slips', page: 'slips' },
      { key: 'drystack-open-button', label: 'Dry stack', page: 'drystack' },
    ],
  },
  'hub-moorings': {
    label: 'Moorings', icon: '/assets/mooring.png',
    tabs: [
      { key: 'mooring-field-rail-button', label: 'Field', page: 'mooringField', countId: 'rail-count-field', desc: 'The mooring field map: place, chain, edit and review positions' },
      { key: 'mooring-planning-open-button', label: 'Season board', page: 'mooringField', mode: 'season', hash: 'season', wire: true, noTab: true, title: 'Season board', desc: 'Week-by-week placing & hauling, with crew capacity (Mooring Field)' },
      { key: 'mooring-menu-open-button', label: 'Rentals', page: 'rentalDatabase', wire: true, title: 'Rental Services', desc: 'Contracts, billing & assignments' },
      { key: 'mooring-database-open-button', label: 'Database', page: 'mooringDatabase', wire: true, title: 'Mooring Database', desc: 'Tackle, owners & inspection history' },
      { key: 'mooring-haul-open-button', label: 'Hauls', page: 'mooringHaul', wire: true, title: 'Mooring Hauls', desc: 'Fall crew tablet · haul queue, repairs & billing' },
      { key: 'mooring-launch-open-button', label: 'Launches', page: 'mooringLaunch', wire: true, title: 'Mooring Launches', desc: 'Spring crew tablet · launch queue, repairs & billing' },
      { key: 'swing-check-open-button', label: 'Swing check', page: 'mooringField', mode: 'swing', hash: 'swing', wire: true, noTab: true, title: 'Swing check', desc: 'Who touches in a given wind and tide (Mooring Field)' },
      { key: 'impact-forecast-open-button', label: 'Impact forecast', page: 'impactForecast', wire: true, title: 'Impact Forecast', desc: 'Storm exposure by field' },
    ],
  },
  'hub-dock': {
    label: 'Dock', icon: '/assets/powerboat.png',
    tabs: [
      { key: 'launch-dispatch-rail-button', label: 'Dispatch', page: 'launchDispatch', countId: 'rail-count-dispatch' },
      { key: 'dock-schedule-open-button', label: 'Schedule', page: 'dockSchedule' },
      { key: 'uscg-open-button', label: 'Launch boats', page: 'uscg', desc: 'USCG readiness, launch checks, drills, crew and each launch’s COI' },
      { key: 'dock-app-rail-button', label: 'Dock app', external: true, desc: 'The launch crew’s app (opens in a new tab)' },
    ],
  },
  'hub-fuel': {
    label: 'Fuel', icon: '/assets/gas-pump.svg',
    tabs: [
      { key: 'fuel-reading-open-button', label: 'Readings', page: 'fuel-reading', wire: true, title: 'Fuel Readings', desc: 'Log tank sticks & pump totals' },
      { key: 'fuel-dashboard-open-button', label: 'Dashboard', page: 'fuel-dashboard', wire: true, title: 'Fuel Dashboard', desc: 'Volume, margin & compliance' },
    ],
  },
  'hub-inbox': {
    label: 'Inbox', icon: '/assets/inbox.svg',
    tabs: [
      { key: 'requests-open-button', label: 'Requests', page: 'requests', countId: 'rail-count-requests' },
      { key: 'messages-rail-button', label: 'Messages', page: 'messages', countId: 'rail-count-messages' },
      { key: 'tech-questions-rail-button', label: 'Tech questions', page: 'techQuestions', countId: 'rail-count-techq' },
      { key: 'part-requests-rail-button', label: 'Part requests', page: 'partRequests', countId: 'rail-count-partreq' },
    ],
  },
};

const MEMBERS = new Map(); // tab key → { hub, tab }
Object.entries(HUBS).forEach(([hub, h]) => h.tabs.forEach(tab => MEMBERS.set(tab.key, { hub, tab })));

export const HUB_KEYS = Object.keys(HUBS);
export const MEMBER_KEYS = [...MEMBERS.keys()];
export const isHub = key => !!HUBS[key];
export const hubOf = key => MEMBERS.get(key)?.hub || null;
export const memberTab = key => MEMBERS.get(key)?.tab || null;

const LAST_KEY = 'railHubTab:v1';
const readLast = () => { try { return JSON.parse(localStorage.getItem(LAST_KEY) || '{}') || {}; } catch { return {}; } };
function storeLast(hub, key) {
  const last = readLast();
  if (last[hub] === key) return;
  last[hub] = key;
  try { localStorage.setItem(LAST_KEY, JSON.stringify(last)); } catch { /* storage blocked */ }
}

/** The tabs a hub shows now: its pages that are in this app (not removed for the role) and not switched off. */
export function hubTabs(hub) {
  return (HUBS[hub]?.tabs || []).filter(t => {
    if (t.noTab) return false;
    const el = document.getElementById(t.key);
    return el && !el.classList.contains('feature-off');
  });
}

let popouts = new Set(); // member keys with their own rail link (railLayout.js)
let cur = null;          // { hub, key, popout } — the hub page on screen
let viaHub = false;      // the next member click came from a hub or a tab, not its own rail link

/** railLayout.js: which pages have their own rail link (they stay tabs as well). */
export function setPopouts(keys) {
  popouts = new Set(keys || []);
  schedule();
}

/** Hub links follow their pages: removed with them (role), switched off with them (Settings › Features). */
export function syncHubs() {
  HUB_KEYS.forEach(hub => {
    const btn = document.getElementById(hub);
    if (!btn) return;
    if (!HUBS[hub].tabs.some(t => document.getElementById(t.key))) { btn.remove(); return; }
    btn.classList.toggle('feature-off', !hubTabs(hub).length);
  });
  paintCounts();
  schedule();
}

/** Open a hub on the page last used in it (or its first). */
export function openHub(hub) {
  const tabs = hubTabs(hub).filter(t => !t.external);
  if (!tabs.length) return;
  const tab = tabs.find(t => t.key === readLast()[hub]) || tabs[0];
  open(hub, tab);
}

function open(hub, tab) {
  if (!tab.external) {
    cur = { hub, key: tab.key, popout: false };
    storeLast(hub, tab.key);
    paint(); // the tab bar paints now, not once the page has loaded
  }
  viaHub = true;
  document.getElementById(tab.key)?.click();
  viaHub = false;
}

/** After any page opens: find its hub and tab and paint them (app.js calls it from showPage). */
export function selectHubTab() {
  schedule();
}

// After the click that opened the page has finished: app.js's generic rail-button listener marks
// the clicked (off-stage) button selected, and this has to win over it.
let timer = 0;
function schedule() {
  if (!timer) timer = setTimeout(() => { timer = 0; sync(); }, 0);
}

const visiblePage = () => [...document.querySelectorAll('.page-view')].find(el => el.style.display === 'flex')?.id || null;

/** The tab for a page on screen (a page's modes, like Mooring Field's Season board, are its own tab). */
function tabForPage(page) {
  return [...MEMBERS.values()].find(m => m.tab.page === page && !m.tab.noTab && document.getElementById(m.tab.key)) || null;
}

function sync() {
  const page = visiblePage();
  const mine = cur && memberTab(cur.key)?.page === page;
  if (!mine) {
    const m = page && tabForPage(page);
    cur = m ? { hub: m.hub, key: m.tab.key, popout: popouts.has(m.tab.key) } : null;
    if (cur) storeLast(cur.hub, cur.key);
  }
  paint();
}

function onMemberClick(key) {
  const m = MEMBERS.get(key);
  if (!m) return;
  if (m.tab.noTab) { cur = null; schedule(); return; } // its page's own tab is selected
  if (!m.tab.external) {
    cur = { hub: m.hub, key, popout: !viaHub && popouts.has(key) };
    storeLast(m.hub, key);
  }
  schedule();
}

/* ---------- painting ---------- */

function paint() {
  const bar = document.getElementById('hub-bar');
  if (cur) {
    document.querySelectorAll('#buttonBar .selected-tide-button').forEach(b => b.classList.remove('selected-tide-button'));
    document.getElementById(cur.popout ? cur.key : cur.hub)?.classList.add('selected-tide-button');
  }
  const tabs = cur ? hubTabs(cur.hub) : [];
  const show = tabs.length > 1;
  document.body.classList.toggle('hub-tabs-on', show);
  if (!bar) return;
  if (bar.hidden !== !show) bar.hidden = !show;
  if (!show) return;
  document.getElementById('hub-bar-title').textContent = HUBS[cur.hub].label;
  const list = document.getElementById('hub-tabs');
  const focused = list.contains(document.activeElement) ? document.activeElement.dataset.key : null;
  const html = tabs.map(t => {
    const on = t.key === cur.key;
    const n = countFor(t);
    return `<button type="button" role="tab" class="hub-tab${t.external ? ' hub-tab-ext' : ''}" data-key="${escapeHTML(t.key)}"
      aria-selected="${on}" tabindex="${on ? 0 : -1}"${t.desc ? ` title="${escapeHTML(t.desc)}"` : ''}>
      <span>${escapeHTML(t.label)}</span>${n ? `<span class="rail-count hub-tab-count">${escapeHTML(n.text)}</span>` : ''}${t.external ? '<span class="hub-tab-arrow" aria-hidden="true">↗</span>' : ''}</button>`;
  }).join('');
  if (list.dataset.html !== html) {
    list.innerHTML = html;
    list.dataset.html = html;
    if (focused) list.querySelector(`[data-key="${CSS.escape(focused)}"]`)?.focus();
  }
}

/* ---------- counts ---------- */

function countFor(tab) {
  const el = tab.countId && document.getElementById(tab.countId);
  if (!el || el.hidden || !el.textContent.trim()) return null;
  return { text: el.textContent.trim(), n: parseInt(el.textContent, 10) || 0, title: el.title || '' };
}

/** A hub's badge is the sum of its pages' counts; its tooltip joins theirs. */
function paintCounts() {
  HUB_KEYS.forEach(hub => {
    const pill = document.getElementById(hub)?.querySelector('.rail-count');
    if (!pill) return;
    const parts = hubTabs(hub).map(countFor).filter(Boolean);
    const n = parts.reduce((t, p) => t + p.n, 0);
    const text = n > 99 ? '99+' : String(n || '');
    if (pill.hidden !== !n) pill.hidden = !n;
    if (pill.textContent !== text) pill.textContent = text;
    const title = parts.map(p => p.title).filter(Boolean).join(' · ');
    if (pill.title !== title) pill.title = title;
  });
  if (cur) paint();
}

/* ---------- tabs ---------- */

function wireTabs() {
  const list = document.getElementById('hub-tabs');
  if (!list) return;
  list.addEventListener('click', e => {
    const b = e.target.closest('.hub-tab');
    if (!b || !cur) return;
    const tab = memberTab(b.dataset.key);
    if (tab) open(cur.hub, tab);
  });
  // Roving tabindex: arrows and Home/End move between tabs; Enter/Space open (native button).
  list.addEventListener('keydown', e => {
    const tabs = [...list.querySelectorAll('.hub-tab')];
    const i = tabs.indexOf(document.activeElement);
    if (i < 0) return;
    let j = null;
    if (e.key === 'ArrowRight') j = (i + 1) % tabs.length;
    if (e.key === 'ArrowLeft') j = (i - 1 + tabs.length) % tabs.length;
    if (e.key === 'Home') j = 0;
    if (e.key === 'End') j = tabs.length - 1;
    if (j == null) return;
    e.preventDefault();
    tabs.forEach((t, k) => { t.tabIndex = k === j ? 0 : -1; });
    tabs[j].focus();
  });
}

/* ---------- init ---------- */

let started = false;
export function initRailHubs() {
  if (started) return;
  started = true;
  HUB_KEYS.forEach(hub => document.getElementById(hub)?.addEventListener('click', () => openHub(hub)));
  MEMBERS.forEach((m, key) => document.getElementById(key)?.addEventListener('click', () => onMemberClick(key)));
  wireTabs();
  // Counts change (shellNav.js) and members leave (role) — keep hub badges and links in step.
  const counts = HUB_KEYS.flatMap(h => HUBS[h].tabs).map(t => t.countId && document.getElementById(t.countId)).filter(Boolean);
  const mo = new MutationObserver(paintCounts);
  counts.forEach(c => mo.observe(c, { attributes: true, attributeFilter: ['hidden', 'title'], childList: true, characterData: true, subtree: true }));
  // The page on screen changes (rail, search, goTo from another page, deep links).
  const pages = new MutationObserver(schedule);
  document.querySelectorAll('.page-view').forEach(p => pages.observe(p, { attributes: true, attributeFilter: ['style'] }));
  syncHubs();
}
